import { useState, useCallback } from 'react';

/**
 * Tracks whether a specific user has already seen the snapshot guidance banner.
 * Keyed per user email so different users on the same browser each get the
 * first-time experience independently.
 *
 * Flow:
 *   - First open: hasSeen = false → inline banner is shown.
 *   - User dismisses with X → markAsSeen() → localStorage updated, hasSeen becomes true.
 *   - Next open: hasSeen = true → banner hidden, ⓘ tooltip shown instead.
 *
 * ─── DEV TESTING FLAGS (.env.local) ─────────────────────────────────────────
 *  VITE_SNAPSHOT_GUIDANCE_FORCE_SEEN=true   → forces return-visit state (tooltip only, no banner)
 *  VITE_SNAPSHOT_GUIDANCE_FORCE_NEW=true    → forces first-time state (banner always shown)
 *
 *  Add either line to your .env.local file (never commit it). Restart the dev
 *  server after changing. Both flags are ignored in production builds.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useSnapshotGuidanceSeen(userEmail: string | null): {
  hasSeen: boolean;
  markAsSeen: () => void;
  resetSeen: () => void;
} {
  const key = userEmail ? `snapshot_guidance_seen_${userEmail}` : null;

  const [hasSeen, setHasSeen] = useState<boolean>(() => {
    if (import.meta.env.DEV) {
      if (import.meta.env['VITE_SNAPSHOT_GUIDANCE_FORCE_SEEN'] === 'true') return true;
      if (import.meta.env['VITE_SNAPSHOT_GUIDANCE_FORCE_NEW'] === 'true') return false;
    }
    return key ? localStorage.getItem(key) === 'true' : false;
  });

  const markAsSeen = useCallback((): void => {
    if (key) {
      localStorage.setItem(key, 'true');
      setHasSeen(true);
    }
  }, [key]);

  const resetSeen = useCallback((): void => {
    if (key) {
      localStorage.removeItem(key);
      setHasSeen(false);
    }
  }, [key]);

  return { hasSeen, markAsSeen, resetSeen };
}
